import { useEffect, useRef, useState } from "react";
import { X, ListPlus } from "lucide-react";
import { Mascot } from "@/components/Mascot";
import { AddToCollectionDialog } from "@/components/AddToCollectionDialog";
import { onCafeSaved, type SavedCafe } from "@/lib/saveConfirmation";
import { cn } from "@/lib/utils";

// How long the confirmation stays before auto-dismissing.
const VISIBLE_MS = 6000;
// Matches the slide-out transition duration so we unmount after it animates out.
const SLIDE_MS = 300;

/**
 * Branded, transient confirmation shown when a café is newly saved (from a
 * browse card, the café page, or an empty-state suggestion). Mirrors the
 * ActivityBanner's espresso/cream/mascot styling, and offers a one-tap
 * "Add to list" that opens the existing collection picker for that café.
 */
export function SaveConfirmation() {
  const [cafe, setCafe] = useState<SavedCafe | null>(null);
  const [shown, setShown] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (unmountTimer.current) clearTimeout(unmountTimer.current);
  };

  const dismiss = () => {
    clearTimers();
    setShown(false);
    unmountTimer.current = setTimeout(() => setCafe(null), SLIDE_MS);
  };

  useEffect(() => {
    const unsubscribe = onCafeSaved((saved) => {
      clearTimers();
      setCafe(saved);
      // Mount first, then flip `shown` next frame so the slide-in animates.
      requestAnimationFrame(() => setShown(true));
      hideTimer.current = setTimeout(dismiss, VISIBLE_MS);
    });
    return () => {
      unsubscribe();
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!cafe) return null;

  // Open the list picker; hide the snackbar but keep the café around for the dialog.
  const openList = () => {
    clearTimers();
    setShown(false);
    setListOpen(true);
  };

  return (
    <>
      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center px-3 pt-[calc(env(safe-area-inset-top)+0.5rem)]"
        role="status"
        aria-live="polite"
      >
        <div
          className={cn(
            "pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-lift transition-all duration-300 ease-out",
            shown ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0",
          )}
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-secondary">
            <Mascot mood="happy" size="sm" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs text-muted-foreground">Saved to your spots</span>
            <span className="block truncate text-sm font-semibold text-foreground">{cafe.cafeName}</span>
          </span>
          <button
            type="button"
            onClick={openList}
            className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-sage px-3.5 py-2 text-xs font-semibold text-cream transition-colors hover:bg-sage/90"
          >
            <ListPlus className="h-3.5 w-3.5" />
            Add to list
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <AddToCollectionDialog
        cafeId={cafe.cafeId}
        cafeName={cafe.cafeName}
        open={listOpen}
        onOpenChange={(open) => {
          setListOpen(open);
          if (!open) dismiss();
        }}
      />
    </>
  );
}
